import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import TradingActionBoard from './TradingActionBoard';
import V1PipelineStrip from './V1PipelineStrip';
import ServerErrorBanner, { useServerErrors } from './ServerErrorBanner';
import { formatIstDateTime } from './ist-datetime';
import IstClock from './IstClock';
import { DAILY_POOL_CAP, LIVE_SLOT_COUNT, PER_SHARE_PROFIT, HOLD_ESCAPE_MIN_RS, HOLD_ESCAPE_MAX_RS, HOLD_SOFT_AFTER_MIN, HOLD_STOP_AFTER_MIN, HOLD_STOP_LOSS_RS, SELL_TAB_AUTO_SOLD_SEC, fmtRsPerShare, UNLIMITED_CAPITAL, MIN_SHARE_PRICE, MAX_SHARE_PRICE, attachHoldTiming, attachSellTiming, isTimedOut, qualifiesHoldTabSell, MAX_HOLD_MINUTES, STOCK_QTY, applyV1MasterSettings } from './trading-rules';
import {
  armSellAudioAutoUnlock,
  fireSellAlert,
  playMovingToHoldVoice,
  playSellBuzz,
  playSoldDoneChime,
  playStopLossSoldVoice,
  requestSellNotifications,
  warmSellAudio
} from './sell-alert-audio';
import './NormalTrading.css';
import './NormalTradingV1.css';

const API_TIMEOUT_MS = 28000;
const LIVE_POLL_MS = 8000;
const PRICE_POLL_MS = 8000;
const SOLD_TODAY_POLL_MS = 30000;
const FAIL_BEFORE_ALERT = 5;

function boardHasContent(b) {
  if (!b) return false;
  const al = b.actionLists;
  if (!al) return Boolean(b.v1Pipeline || b.lastAction);
  return Boolean(
    (al.buy && al.buy.length)
    || (al.hold && al.hold.length)
    || (al.sell && al.sell.length)
    || b.v1Pipeline
  );
}

function quotesToPriceMap(data) {
  const map = {};
  const rows = data?.quotes || [];
  for (const q of rows) {
    const key = (q.stock || q.symbol || '').replace(/\.NS$/i, '');
    if (!key || !q.price) continue;
    map[key] = { ...q, stock: key, fetchedAt: q.fetchedAt || Date.now() };
  }
  if (data?.prices && typeof data.prices === 'object') {
    for (const [key, q] of Object.entries(data.prices)) {
      if (q?.price) map[key.replace(/\.NS$/i, '')] = q;
    }
  }
  return map;
}

function patchItemPrices(item, priceMap) {
  if (!item?.stock) return item;
  const q = priceMap[item.stock];
  if (!q?.price) return item;
  const currentPrice = q.price;
  const bid = q.bid ?? q.sellBrokerPrice ?? currentPrice;
  const ask = q.ask ?? q.buyBrokerPrice ?? currentPrice;
  const entry = item.botPrice ?? item.entryPrice ?? item.price;
  const move = entry != null
    ? Math.round((Number(currentPrice) - Number(entry)) * 100) / 100
    : item.move;
  const brokerSellMove = entry != null && bid != null
    ? Math.round((Number(bid) - Number(entry)) * 100) / 100
    : null;
  return {
    ...item,
    currentPrice,
    bid,
    ask,
    buyBrokerPrice: ask,
    sellBrokerPrice: bid,
    volume: q.volume ?? item.volume,
    bidSize: q.bidSize ?? item.bidSize,
    askSize: q.askSize ?? item.askSize,
    move,
    perShareMove: move,
    brokerSellMove,
    live: true
  };
}

function liveStatusLabel(st, marketOpen, totalDay = 0, profitTarget = 100) {
  void profitTarget;
  if (marketOpen === true && st !== 'disabled') {
    return { cls: 'running', text: totalDay > 0 ? 'LIVE · EARN MORE' : 'LIVE' };
  }
  if (st === 'running' || st === 'waiting' || st === 'target_hit') {
    return { cls: 'running', text: 'LIVE · EARN MORE' };
  }
  if (st === 'market_closed' || st === 'weekend') return { cls: 'weekend', text: 'MARKET CLOSED' };
  return { cls: 'running', text: 'LIVE · EARN MORE' };
}

function itemOpenedAt(item) {
  if (item?.openedAt) return item.openedAt;
  if (item?.previewOpenedAt) return new Date(item.previewOpenedAt).toISOString();
  return null;
}

function shouldMoveToHold(item, move) {
  const openedAt = itemOpenedAt(item);
  if (!openedAt) return false;
  if (move != null && move >= PER_SHARE_PROFIT - 0.004) return false;
  return isTimedOut({ openedAt }, MAX_HOLD_MINUTES);
}

function buyTimedOutWeakOrPartial(item, move) {
  return shouldMoveToHold(item, move);
}

function itemHoldSince(item) {
  return item?.holdSince || itemOpenedAt(item);
}

function classifyLiveTab(item, priceMap, sellAlertsOpen = true) {
  const patched = patchItemPrices(item, priceMap);
  const entry = patched.botPrice ?? patched.entryPrice ?? patched.price;
  const move = patched.move ?? (
    entry != null && patched.currentPrice != null
      ? Math.round((Number(patched.currentPrice) - Number(entry)) * 100) / 100
      : null
  );
  const withMove = move != null ? { ...patched, move, perShareMove: move } : patched;
  const openedAt = itemOpenedAt(withMove);
  const holdSince = itemHoldSince(withMove);
  const withOpen = openedAt ? { ...withMove, openedAt, holdSince } : withMove;

  const asSell = (row, { urgent = false, reason } = {}) => ({
    tab: 'sell',
    item: {
      ...row,
      kind: urgent ? 'urgent' : 'rotate',
      liveSellAlert: sellAlertsOpen,
      sellAlertsMuted: !sellAlertsOpen,
      workflowTab: 'sell',
      reason: reason || (sellAlertsOpen
        ? `+₹${row.move?.toFixed(2)}/sh — sell on Groww`
        : 'After 3:15 PM IST — sell tab (alerts muted until 9:15 AM)')
    }
  });

  const asHold = (row, reason) => ({
    tab: 'hold',
    item: {
      ...row,
      workflowTab: 'hold',
      holdSince: row.holdSince || new Date().toISOString(),
      reason: reason || `10 min — no ${fmtRsPerShare(PER_SHARE_PROFIT)}/sh — HOLD (Rule 2)`
    }
  });

  // Groww mirror picks (pending)
  if (patched.pending) {
    if (move != null && move >= PER_SHARE_PROFIT - 0.004) {
      return asSell(withOpen, {
        urgent: true,
        reason: `+₹${move.toFixed(2)}/sh ≥ ${fmtRsPerShare(PER_SHARE_PROFIT)}/sh — sell on Groww now`
      });
    }
    if (shouldMoveToHold(withOpen, move)) {
      return asHold(withOpen);
    }
    return { tab: 'buy', item: withOpen };
  }

  const wfTab = patched.workflowTab || 'buy';

  if (wfTab === 'buy' && move != null && move >= PER_SHARE_PROFIT - 0.004) {
    return asSell(withOpen, {
      urgent: true,
      reason: `+₹${move.toFixed(2)}/sh ≥ ${fmtRsPerShare(PER_SHARE_PROFIT)}/sh — sell on Groww now`
    });
  }
  if (wfTab === 'buy' && shouldMoveToHold(withOpen, move)) {
    return asHold(withOpen);
  }
  const holdSellOpts = {
    profitRs: withOpen.buyProfitRs ?? PER_SHARE_PROFIT,
    escapeMin: withOpen.holdEscapeMinRs ?? HOLD_ESCAPE_MIN_RS,
    escapeMax: withOpen.holdEscapeMaxRs ?? HOLD_ESCAPE_MAX_RS,
    softAfterMin: withOpen.holdSoftAfterMin ?? HOLD_SOFT_AFTER_MIN,
    stopAfterMin: withOpen.holdStopAfterMin ?? HOLD_STOP_AFTER_MIN
  };
  if (wfTab === 'hold' && move != null && qualifiesHoldTabSell(move, holdSince, Date.now(), holdSellOpts)) {
    const pt = holdSellOpts.profitRs;
    return asSell(withOpen, {
      urgent: true,
      reason: move >= pt - 0.004
        ? `HOLD → +₹${move.toFixed(2)}/sh (≥${fmtRsPerShare(pt)}/sh Master) — SELL tab`
        : `HOLD → +₹${move.toFixed(2)}/sh (Master soft/late rule) — SELL tab`
    });
  }
  if (wfTab === 'hold') return { tab: 'hold', item: withOpen };
  if (wfTab === 'sell') {
    return asSell(withOpen, {
      urgent: move != null && (
        move >= PER_SHARE_PROFIT - 0.004
        || (move >= HOLD_ESCAPE_MIN_RS - 0.004 && move <= HOLD_ESCAPE_MAX_RS + 0.004)
      ),
      reason: withOpen.reason
    });
  }
  return { tab: 'buy', item: withOpen };
}

function buildActionLists(lists, priceMap, nowMs = Date.now(), sellAlertsOpen = true) {
  const buy = [];
  const hold = [];
  const sell = [];
  const seen = new Set();

  const ingest = (item) => {
    const key = item.stock || item.symbol;
    if (!key || seen.has(key)) return;
    seen.add(key);
    const { tab, item: row } = classifyLiveTab(item, priceMap, sellAlertsOpen);
    if (tab === 'sell') {
      sell.push(attachSellTiming({
        ...row,
        holdSoftAfterMin: row.holdSoftAfterMin ?? HOLD_SOFT_AFTER_MIN,
        holdStopAfterMin: row.holdStopAfterMin ?? HOLD_STOP_AFTER_MIN,
        sellWaitSec: row.sellWaitSec ?? SELL_TAB_AUTO_SOLD_SEC
      }, nowMs));
    } else if (tab === 'hold') {
      hold.push(attachHoldTiming({
        ...row,
        holdSoftAfterMin: row.holdSoftAfterMin ?? HOLD_SOFT_AFTER_MIN,
        holdStopAfterMin: row.holdStopAfterMin ?? HOLD_STOP_AFTER_MIN
      }, 'hold', nowMs));
    } else {
      buy.push(attachHoldTiming(row, 'buy', nowMs));
    }
  };

  for (const item of lists.sell || []) ingest(item);
  for (const item of lists.hold || []) ingest(item);
  for (const item of lists.buy || []) ingest(item);

  const holdSortMs = (row) => (
    Date.parse(row?.holdSince || row?.openedAt || '')
    || Number(row?.lastUpdated)
    || 0
  );
  hold.sort((a, b) => holdSortMs(b) - holdSortMs(a));

  return { buy, hold, sell };
}

async function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

export default function NormalTradingV1() {
  const [status, setStatus] = useState(null);
  const [soldTodayList, setSoldTodayList] = useState([]);
  const [soldTodayRefreshing, setSoldTodayRefreshing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [connection, setConnection] = useState('connecting');
  const [livePrices, setLivePrices] = useState({ status: 'idle', prices: {}, prevPrices: {}, priceSource: null });
  const [holdClock, setHoldClock] = useState(() => Date.now());
  const { errors: serverErrors, pushError, clearSource, dismiss: dismissServerError, clearAll: clearAllServerErrors } = useServerErrors();

  const enabledRef = useRef(false);
  const boardInFlightRef = useRef(false);
  const pricesInFlightRef = useRef(false);
  const failRef = useRef({ board: 0, prices: 0, health: 0 });
  const lastBoardRef = useRef(null);
  const sellUrgentBeepedRef = useRef(new Set());
  const holdAnnouncedRef = useRef(new Set());
  const stopLossAnnouncedRef = useRef(new Set());
  const sellBuzzIntervalRef = useRef(null);
  const soldTodayCountRef = useRef(0);
  const holdWarmRef = useRef(false);
  const liveStreamRef = useRef(null);

  const soldTodayInFlightRef = useRef(false);
  const soldTodayPendingRef = useRef(false);

  const loadSoldToday = useCallback(async () => {
    if (soldTodayInFlightRef.current) {
      soldTodayPendingRef.current = true;
      return;
    }
    soldTodayInFlightRef.current = true;
    setSoldTodayRefreshing(true);
    try {
      const res = await axios.get('/api/normal-v1/sold-history', { timeout: 15000 });
      const rows = res.data?.soldToday || [];
      const list = Array.isArray(rows) ? rows : [];
      list.sort((a, b) => {
        const ta = Number(a?.lastUpdated) || Date.parse(a?.savedAt || a?.sellDateTime || '') || 0;
        const tb = Number(b?.lastUpdated) || Date.parse(b?.savedAt || b?.sellDateTime || '') || 0;
        return tb - ta;
      });
      setSoldTodayList(list);
    } catch {
      /* keep last good sold-today list */
    } finally {
      soldTodayInFlightRef.current = false;
      setSoldTodayRefreshing(false);
      if (soldTodayPendingRef.current) {
        soldTodayPendingRef.current = false;
        setTimeout(() => {
          loadSoldToday();
        }, 100);
      }
    }
  }, []);

  const applyBoard = useCallback((data) => {
    if (!data) return;
    if (data.v1Master) applyV1MasterSettings(data.v1Master);
    lastBoardRef.current = data;
    setStatus(data);
    // Keep Sold Today in sync — prefer merged board rows when fresher/longer
    const boardSold = data?.actionLists?.soldToday;
    if (Array.isArray(boardSold) && boardSold.length) {
      setSoldTodayList((prev) => {
        const prevN = prev?.length || 0;
        if (boardSold.length >= prevN) return boardSold;
        return prev;
      });
    }
    if (boardHasContent(data)) {
      setConnection(data.stale ? 'stale' : 'ok');
    } else if (data.stale) {
      setConnection('stale');
    } else {
      setConnection('ok');
    }
  }, []);

  const loadLiveBoard = useCallback(async (forceFresh = false) => {
    if (boardInFlightRef.current) return lastBoardRef.current;
    boardInFlightRef.current = true;
    try {
      const res = await axios.get('/api/normal-v1/live-board', {
        timeout: API_TIMEOUT_MS,
        params: forceFresh ? { warm: 1 } : undefined
      });
      applyBoard(res.data);
      failRef.current.board = 0;
      if (boardHasContent(res.data)) {
        clearSource('v1-live-board');
        clearSource('bootstrap');
      }
      if (res.data.liveConnection?.ok === false && failRef.current.prices >= FAIL_BEFORE_ALERT) {
        pushError('live-connection', `Live quotes slow (${res.data.liveConnection.failures || 0} failures) — bot still running`);
      } else {
        clearSource('live-connection');
      }
      return res.data;
    } catch (e) {
      failRef.current.board += 1;
      setConnection(lastBoardRef.current ? 'stale' : 'reconnecting');
      if (failRef.current.board >= FAIL_BEFORE_ALERT) {
        pushError('v1-live-board', e);
      }
      return lastBoardRef.current;
    } finally {
      boardInFlightRef.current = false;
    }
  }, [applyBoard, clearSource, pushError]);

  const refreshLivePrices = useCallback(async (forceFresh = false) => {
    if (pricesInFlightRef.current) return;
    pricesInFlightRef.current = true;
    try {
      const res = await axios.get('/api/normal/live-prices', {
        timeout: 10000,
        params: forceFresh ? { fresh: 1 } : undefined
      });
      const map = quotesToPriceMap(res.data);
      const hasQuotes = Object.keys(map).length > 0;
      setLivePrices({
        status: hasQuotes ? (res.data?.stale ? 'stale' : 'live') : 'reconnecting',
        prices: map,
        prevPrices: res.data.prevPrices || {},
        priceSource: res.data.priceSource || res.data.source || (hasQuotes ? 'yahoo_live' : null)
      });
      if (hasQuotes) {
        failRef.current.prices = 0;
        clearSource('live-prices');
        clearSource('bootstrap');
      } else {
        failRef.current.prices += 1;
        if (failRef.current.prices >= FAIL_BEFORE_ALERT) {
          pushError('live-prices', 'Live prices empty — retrying');
        }
      }
    } catch (e) {
      failRef.current.prices += 1;
      setLivePrices((prev) => ({ ...prev, status: 'reconnecting' }));
      if (failRef.current.prices >= FAIL_BEFORE_ALERT) {
        pushError('live-prices', e);
      }
    } finally {
      pricesInFlightRef.current = false;
    }
  }, [clearSource, pushError]);

  const applyLiveStreamPayload = useCallback((data) => {
    const map = quotesToPriceMap(data);
    const hasQuotes = Object.keys(map).length > 0;
    if (!hasQuotes) return;
    setLivePrices({
      status: data?.stale ? 'stale' : 'live',
      prices: map,
      prevPrices: data?.prevPrices || {},
      priceSource: data?.priceSource || data?.source || 'yahoo_live'
    });
    failRef.current.prices = 0;
    clearSource('live-prices');
    clearSource('bootstrap');
  }, [clearSource]);

  const pingHealth = useCallback(async () => {
    try {
      await axios.get('/api/normal-v1/health', { timeout: 5000 });
      failRef.current.health = 0;
      clearSource('health');
    } catch (e) {
      failRef.current.health += 1;
      if (failRef.current.health >= FAIL_BEFORE_ALERT) {
        pushError('health', e);
      }
    }
  }, [clearSource, pushError]);

  const bootstrapLive = useCallback(async () => {
    for (let attempt = 0; attempt < 5; attempt += 1) {
      const board = await loadLiveBoard(attempt === 0);
      await refreshLivePrices(true);
      if (boardHasContent(board)) {
        clearSource('bootstrap');
        clearSource('v1-live-board');
        return;
      }
      await sleep(1500 * (attempt + 1));
    }
    pushError('bootstrap', 'Could not reach live board after retries — keep this tab open, auto-retrying');
  }, [loadLiveBoard, refreshLivePrices, clearSource, pushError]);

  const refreshBoard = async () => {
    await warmSellAudio();
    setBusy(true);
    try {
      await Promise.all([loadLiveBoard(true), refreshLivePrices(true), loadSoldToday(), pingHealth()]);
      clearSource('refresh');
    } finally {
      setBusy(false);
    }
  };

  const resetBuyHold = async () => {
    if (!window.confirm('Clear BUY & HOLD tabs and start fresh monitoring?\n\nSold today will NOT be removed.')) return;
    setBusy(true);
    try {
      const res = await axios.post('/api/normal-v1/reset-buy-hold', {}, { timeout: API_TIMEOUT_MS });
      if (res.data?.message) clearSource('reset-buy-hold');
      await Promise.all([loadLiveBoard(true), refreshLivePrices(true)]);
    } catch (e) {
      pushError('reset-buy-hold', e.response?.data?.message || e.message || 'Reset failed');
    } finally {
      setBusy(false);
    }
  };

  const setBuyConfirmMode = async (mode) => {
    const labels = {
      'candle-confirm': 'Roll back to candle confirm only?\n\nFee-aware (RS if Nifty weak + volume + hard net) will turn OFF.\nCandle UP filter stays ON.',
      'fee-aware-momentum': 'Enable fee-aware momentum?\n\nEinstein → candle UP → (if Nifty down: stock must lead) → volume → net≥min → BUY'
    };
    if (!window.confirm(labels[mode] || `Switch BUY mode to ${mode}?`)) return;
    setBusy(true);
    try {
      const res = await axios.post('/api/normal-v1/buy-confirm-mode', { mode }, { timeout: API_TIMEOUT_MS });
      clearSource('buy-confirm-mode');
      if (res.data?.message) {
        /* ok */
      }
      await loadLiveBoard(true);
    } catch (e) {
      pushError('buy-confirm-mode', e.response?.data?.message || e.message || 'Strategy switch failed');
    } finally {
      setBusy(false);
    }
  };

  const buyConfirmMode = status?.v1Pipeline?.buyConfirmMode
    || (status?.v1Master?.feeAwareMomentum === false
      ? (status?.v1Master?.candleConfirmBuy === false ? 'quote-only' : 'candle-confirm')
      : 'fee-aware-momentum');

  useEffect(() => {
    try {
      localStorage.removeItem('normal-trading-v1-mode');
    } catch {
      /* ignore */
    }
    document.body.classList.add('nt-body');
    return () => document.body.classList.remove('nt-body');
  }, []);

  useEffect(() => {
    if (!enabledRef.current) {
      enabledRef.current = true;
      axios.post('/api/normal/enable', {}, { timeout: 8000 }).catch((e) => {
        if (failRef.current.board >= FAIL_BEFORE_ALERT) pushError('enable', e);
      });
    }
    let cancelled = false;
    bootstrapLive();
    loadSoldToday();
    const boardTimer = setInterval(() => loadLiveBoard(false), LIVE_POLL_MS);
    const priceTimer = setInterval(() => refreshLivePrices(false), PRICE_POLL_MS);
    const soldTodayTimer = setInterval(loadSoldToday, SOLD_TODAY_POLL_MS);
    const healthTimer = setInterval(pingHealth, 15000);

    const connectStream = () => {
      if (liveStreamRef.current) {
        try { liveStreamRef.current.close(); } catch { /* ignore */ }
      }
      const es = new EventSource('/api/normal/live-stream');
      liveStreamRef.current = es;
      es.onmessage = (ev) => {
        try {
          applyLiveStreamPayload(JSON.parse(ev.data));
        } catch { /* ignore */ }
      };
      es.onerror = () => {
        setLivePrices((prev) => ({ ...prev, status: 'reconnecting' }));
        try { es.close(); } catch { /* ignore */ }
        liveStreamRef.current = null;
        if (!cancelled) setTimeout(connectStream, 3000);
      };
    };
    connectStream();
    refreshLivePrices(true);

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        loadLiveBoard(false);
        refreshLivePrices(true);
        loadSoldToday();
        if (!liveStreamRef.current) connectStream();
      }
    };
    const onOnline = () => {
      connectStream();
      loadLiveBoard(false);
      refreshLivePrices(true);
      loadSoldToday();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);

    return () => {
      cancelled = true;
      clearInterval(boardTimer);
      clearInterval(priceTimer);
      clearInterval(soldTodayTimer);
      clearInterval(healthTimer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
      if (liveStreamRef.current) {
        try { liveStreamRef.current.close(); } catch { /* ignore */ }
        liveStreamRef.current = null;
      }
    };
  }, [bootstrapLive, loadLiveBoard, loadSoldToday, refreshLivePrices, pingHealth, pushError, applyLiveStreamPayload]);

  useEffect(() => {
    const timer = setInterval(() => setHoldClock(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const lists = status?.actionLists || {};
  const priceMap = livePrices.prices || {};
  const sellAlertsOpen = status?.sellAlertsOpen !== false;

  const { buy: buyList, hold: holdList, sell: sellList } = useMemo(
    () => buildActionLists(lists, priceMap, holdClock, sellAlertsOpen),
    [lists, priceMap, holdClock, sellAlertsOpen]
  );
  const yesterdayList = useMemo(() => {
    const rows = (lists.yesterday || []).map((item) => patchItemPrices(item, priceMap));
    return [...rows].sort((a, b) => {
      const ta = Number(a?.lastUpdated) || Date.parse(a?.openedAt || a?.holdSince || a?.updatedAt || '') || 0;
      const tb = Number(b?.lastUpdated) || Date.parse(b?.openedAt || b?.holdSince || b?.updatedAt || '') || 0;
      return tb - ta;
    });
  }, [lists.yesterday, priceMap]);

  const stopLossList = useMemo(() => {
    const fromBoard = lists.stopLossToday || [];
    const pool = fromBoard.length
      ? fromBoard
      : (soldTodayList.length ? soldTodayList : (lists.soldToday || [])).filter((r) => {
        if (r?.stopLoss || r?.stopLossTotalRs != null || /HOLD stop|stop-loss|stop loss/i.test(String(r?.reason || ''))) {
          return true;
        }
        const net = r?.netPnl != null ? Number(r.netPnl) : null;
        const gross = r?.pnl != null ? Number(r.pnl) : null;
        return (net != null && net < -0.004) || (gross != null && gross < -0.004);
      });
    return [...pool].sort((a, b) => {
      const ta = Number(a?.lastUpdated) || Date.parse(a?.savedAt || a?.sellDateTime || '') || 0;
      const tb = Number(b?.lastUpdated) || Date.parse(b?.savedAt || b?.sellDateTime || '') || 0;
      return tb - ta;
    });
  }, [lists.stopLossToday, lists.soldToday, soldTodayList]);

  useEffect(() => {
    requestSellNotifications();
    const disarm = armSellAudioAutoUnlock();
    warmSellAudio();
    return disarm;
  }, []);

  const sellActive = sellList.filter((s) => !s.soldToday);
  const sellHistory = soldTodayList.length ? soldTodayList : (lists.soldToday || []);
  // Any profitable share on SELL tab → beep + voice (BUY/HOLD profit rules)
  const sellUrgent = sellAlertsOpen
    ? sellActive.filter((s) => s.move == null || Number(s.move) > 0.004 || s.kind === 'urgent' || s.liveSellAlert)
    : [];

  useEffect(() => {
    if (!sellAlertsOpen) {
      sellUrgentBeepedRef.current.clear();
      if (sellBuzzIntervalRef.current) {
        clearInterval(sellBuzzIntervalRef.current);
        sellBuzzIntervalRef.current = null;
      }
      return;
    }
    for (const row of sellUrgent) {
      const sk = row.stock;
      if (!sk || sellUrgentBeepedRef.current.has(sk)) continue;
      sellUrgentBeepedRef.current.add(sk);
      fireSellAlert({ stock: sk, move: row.move });
    }
    const activeKeys = new Set(sellUrgent.map((r) => r.stock).filter(Boolean));
    for (const sk of [...sellUrgentBeepedRef.current]) {
      if (!activeKeys.has(sk)) sellUrgentBeepedRef.current.delete(sk);
    }
  }, [sellUrgent, sellAlertsOpen]);

  // Sheet: BUY → HOLD voice ("moving to hold tab")
  useEffect(() => {
    const keys = new Set(
      (holdList || []).map((r) => r.stock).filter(Boolean)
    );
    if (!holdWarmRef.current) {
      holdWarmRef.current = true;
      holdAnnouncedRef.current = keys;
      return;
    }
    for (const sk of keys) {
      if (holdAnnouncedRef.current.has(sk)) continue;
      holdAnnouncedRef.current.add(sk);
      playMovingToHoldVoice(sk);
    }
    for (const sk of [...holdAnnouncedRef.current]) {
      if (!keys.has(sk)) holdAnnouncedRef.current.delete(sk);
    }
  }, [holdList]);

  // Sheet stop-loss → Sold Today voice (SELL skipped — needs profit)
  useEffect(() => {
    const rows = sellHistory || [];
    for (const row of rows) {
      if (!row?.stopLoss && !row?.stopLossTotalRs && !/stop/i.test(String(row?.reason || ''))) continue;
      const sk = row.stock;
      const id = row.id || `${sk}:${row.soldAt || row.exitAt || ''}`;
      if (!sk || stopLossAnnouncedRef.current.has(id)) continue;
      stopLossAnnouncedRef.current.add(id);
      const total = row.stopLossTotalRs ?? (row.move != null && row.qty != null
        ? Math.abs(Number(row.move) * Number(row.qty))
        : null);
      playStopLossSoldVoice(sk, total);
    }
  }, [sellHistory]);

  useEffect(() => {
    if (!sellAlertsOpen) return;
    const anyUrgent = sellUrgent.length > 0;
    if (anyUrgent && !sellBuzzIntervalRef.current) {
      sellBuzzIntervalRef.current = setInterval(() => playSellBuzz(), 12000);
    } else if (!anyUrgent && sellBuzzIntervalRef.current) {
      clearInterval(sellBuzzIntervalRef.current);
      sellBuzzIntervalRef.current = null;
    }
  }, [sellUrgent.length, sellAlertsOpen]);

  useEffect(() => {
    const n = sellHistory.length;
    if (n > soldTodayCountRef.current) {
      playSoldDoneChime();
      soldTodayCountRef.current = n;
    } else if (n < soldTodayCountRef.current) {
      soldTodayCountRef.current = n;
    }
  }, [sellHistory.length]);

  useEffect(() => () => {
    if (sellBuzzIntervalRef.current) clearInterval(sellBuzzIntervalRef.current);
  }, []);

  const activeStep = useMemo(() => {
    if (sellActive.length > 0) return 2;
    if (buyList.some((b) => b.stock && !b.pending)) return 1;
    if (buyList.some((b) => b.stock && b.pending)) return 1;
    return 3;
  }, [sellActive.length, buyList]);

  const v1Master = status?.v1Master;
  const profitRs = v1Master?.buyProfitRs ?? PER_SHARE_PROFIT;
  const profitLabel = fmtRsPerShare(profitRs);
  const holdEscapeMin = v1Master?.holdEscapeMinRs ?? HOLD_ESCAPE_MIN_RS;
  const holdEscapeMax = v1Master?.holdEscapeMaxRs ?? HOLD_ESCAPE_MAX_RS;
  const holdMinutes = v1Master?.maxHoldMinutes ?? MAX_HOLD_MINUTES;
  const stockQty = v1Master?.maxBuyQty ?? v1Master?.stockQty ?? STOCK_QTY;
  const minBuyQty = v1Master?.minBuyQty ?? 1;
  const maxBuyQty = v1Master?.maxBuyQty ?? 10;
  const slotsMax = v1Master?.slotsMax ?? LIVE_SLOT_COUNT;
  const priceMin = v1Master?.activeBand?.min ?? MIN_SHARE_PRICE;
  const priceMax = v1Master?.activeBand?.max ?? MAX_SHARE_PRICE;
  const bandLabel = v1Master?.activeBand?.label ?? 'Penny';
  const capitalAmount = v1Master?.capitalAmount ?? status?.capital?.dailyTradingCapital ?? DAILY_POOL_CAP;
  const isUnlimited = v1Master?.unlimitedCapital === true;
  const capital = status?.capital || status?.profitPlan || {};
  const capitalAvailable = Number(capital.capitalAvailable ?? capital.capitalFree ?? capital.cashFree ?? 0);
  const capitalUsed = Number(capital.capitalUsed ?? capital.capitalInUse ?? 0);
  const capitalWallet = Number(capital.walletTotal ?? (capitalAvailable + capitalUsed));
  const totalDay = status?.totalDayPnl ?? ((status?.dailyPnl ?? 0) + (status?.unrealizedPnl ?? 0));
  const profitTarget = status?.profitPlan?.profitTarget ?? status?.settings?.dailyTarget ?? 100;
  const livePill = liveStatusLabel(status?.status, status?.marketOpen, totalDay, profitTarget);
  const istTime = status?.istTime ? formatIstDateTime(status.istTime) : '';
  const connectionHint = connection === 'ok'
    ? null
    : connection === 'stale'
      ? 'Using last good board — refreshing…'
      : 'Connecting to server…';

  return (
    <div className="nt-desktop nt-v1 nt-no-sidebar">
      <main className="nt-main">
        <header className="nt-header nt-header-compact">
          <div>
            <h1>Normal Trading Version 1</h1>
            <p className="nt-subtitle nt-subtitle-desktop">
              {status?.lastAction || `Einstein Brain 100% only · dynamic qty · band ₹${priceMin}–₹${priceMax}`}
            </p>
            <div className="nt-mobile-kpi" aria-label="Session summary">
              <div className="nt-mobile-kpi-cell">
                <span>P&amp;L</span>
                <strong className={totalDay >= 0 ? 'green' : 'red'}>₹{totalDay.toFixed(0)}</strong>
              </div>
              <div className="nt-mobile-kpi-cell">
                <span>Cash</span>
                <strong className="green">₹{capitalAvailable.toLocaleString('en-IN')}</strong>
              </div>
              <div className="nt-mobile-kpi-cell">
                <span>Slots</span>
                <strong>{status?.basket?.slotsUsed ?? 0}/{slotsMax}</strong>
              </div>
              <div className="nt-mobile-kpi-cell">
                <span>Target</span>
                <strong>+{profitLabel}</strong>
              </div>
            </div>
            <p className="nt-v1-header-meta nt-v1-capital-bar nt-desktop-only">
              <span>Capital <strong>₹{Number(capitalWallet || capitalAmount).toLocaleString('en-IN')}</strong></span>
              <span className="nt-v1-cap-sep">·</span>
              <span>Available <strong className="green">₹{capitalAvailable.toLocaleString('en-IN')}</strong></span>
              <span className="nt-v1-cap-sep">·</span>
              <span>In stocks <strong>₹{capitalUsed.toLocaleString('en-IN')}</strong></span>
              <span className="nt-v1-cap-sep">·</span>
              <span>Einstein <strong>100%</strong> only</span>
            </p>
            <p className="nt-v1-header-meta nt-desktop-only">
              {isUnlimited
                ? `₹${priceMin}–₹${priceMax}/sh · qty ${minBuyQty}–${maxBuyQty} · unlimited cash · ${profitLabel}/sh`
                : `Seed ₹${Number(capitalAmount).toLocaleString('en-IN')} · ${slotsMax} slots · qty ${minBuyQty}–${maxBuyQty} · ₹${priceMin}–₹${priceMax}/sh · ${profitLabel}/sh`}
              {' · '}
              P&amp;L today <strong className={totalDay >= 0 ? 'green' : 'red'}>₹{totalDay.toFixed(0)}</strong>
              {' · '}
              <span className="green">earn more</span>
              {istTime ? ` · Server ${istTime}` : ''}
            </p>
            <p className="nt-v1-header-meta nt-desktop-only">
              <Link to="/ledger/normal">Buy/Sell Ledger</Link>
              {' · '}
              <Link to="/NormalTrading/Sold">Sold Today</Link>
              {' · '}
              <Link to="/NormalTrading/Master">V1 Master</Link>
            </p>
          </div>
          <div className="nt-v1-header-right">
            <IstClock />
            <div className={`nt-status-pill ${livePill.cls}`}>
              <span className="nt-status-dot" />
              {livePill.text}
            </div>
          </div>
        </header>

        {connectionHint && (
          <p className="nt-v1-connection-hint" role="status">{connectionHint}</p>
        )}

        <ServerErrorBanner
          errors={serverErrors}
          onDismiss={dismissServerError}
          onClearAll={clearAllServerErrors}
        />

        <section className="nt-v1-toolbar nt-v1-toolbar-actions card mobile-toolbar-row">
          <button type="button" className="nt-v1-btn primary" onClick={refreshBoard} disabled={busy}>
            {busy ? 'Refreshing…' : 'Refresh'}
          </button>
          <button type="button" className="nt-v1-btn" onClick={resetBuyHold} disabled={busy} title="Clear BUY & HOLD only — Sold today stays">
            Reset BUY/HOLD
          </button>
          {buyConfirmMode === 'fee-aware-momentum' ? (
            <button
              type="button"
              className="nt-v1-btn gold"
              onClick={() => setBuyConfirmMode('candle-confirm')}
              disabled={busy}
              title="Turn OFF Nifty/volume/hard-net — keep candle UP confirm only"
            >
              Rollback to candle confirm
            </button>
          ) : (
            <button
              type="button"
              className="nt-v1-btn success"
              onClick={() => setBuyConfirmMode('fee-aware-momentum')}
              disabled={busy}
              title="Einstein → candle UP → Nifty OK → volume → net≥min → BUY"
            >
              Enable fee-aware momentum
            </button>
          )}
          <span className={`nt-v1-strategy-pill ${buyConfirmMode}`} title={status?.v1Pipeline?.strategyNote || ''}>
            {buyConfirmMode === 'fee-aware-momentum' ? 'Fee-aware ON' : buyConfirmMode === 'candle-confirm' ? 'Candle only' : 'Quote only'}
          </span>
          <details className="nt-mobile-more-actions">
            <summary>More</summary>
            <button
              type="button"
              className="nt-v1-btn danger"
              onClick={async () => {
                if (!window.confirm('Fresh start today?\n\n• Clear BUY / HOLD / SELL open positions\n• Set capital to ₹50,000 (V1 + CNC)\n• Keep Sold today / history in backup folder\n• BUY picks wait until 9:15 IST if market closed')) return;
                setBusy(true);
                try {
                  const res = await axios.post('/api/normal-v1/reset-buy-hold', {}, { timeout: API_TIMEOUT_MS });
                  if (res.data?.message) clearSource('reset-buy-hold');
                  await axios.post('/api/normal-v1/master-settings', { capitalAmount: 50000, unlimitedCapital: false }, { timeout: API_TIMEOUT_MS });
                  await Promise.all([loadLiveBoard(true), refreshLivePrices(true)]);
                  try { window.location.reload(); } catch { /* ignore */ }
                } catch (e) {
                  pushError('reset-all', e.response?.data?.message || e.message || 'Reset failed');
                } finally {
                  setBusy(false);
                }
              }}
              disabled={busy}
              title="Clear BUY, HOLD, SELL and YESTERDAY; set capital to ₹50,000"
            >
              Reset screen &amp; ₹50k
            </button>
          </details>
          <button
            type="button"
            className="nt-v1-btn danger nt-desktop-only"
            onClick={async () => {
              if (!window.confirm('Fresh start today?\n\n• Clear BUY / HOLD / SELL open positions\n• Set capital to ₹50,000 (V1 + CNC)\n• Keep Sold today / history in backup folder\n• BUY picks wait until 9:15 IST if market closed')) return;
              setBusy(true);
              try {
                // First clear existing positions and previews so restored cash isn't double-added
                const res = await axios.post('/api/normal-v1/reset-buy-hold', {}, { timeout: API_TIMEOUT_MS });
                if (res.data?.message) clearSource('reset-buy-hold');
                // Then apply master settings to set capital to 50k
                await axios.post('/api/normal-v1/master-settings', { capitalAmount: 50000, unlimitedCapital: false }, { timeout: API_TIMEOUT_MS });
                // Refresh board and prices
                await Promise.all([loadLiveBoard(true), refreshLivePrices(true)]);
                // As a final step, reload to ensure UI shows fresh wallet values
                try { window.location.reload(); } catch { /* ignore */ }
              } catch (e) {
                pushError('reset-all', e.response?.data?.message || e.message || 'Reset failed');
              } finally {
                setBusy(false);
              }
            }}
            disabled={busy}
            title="Clear BUY, HOLD, SELL and YESTERDAY; set capital to ₹50,000"
          >
            Reset screen & set ₹50,000
          </button>
        </section>

        <div className="nt-mobile-pipeline-wrap">
          <V1PipelineStrip pipeline={status?.v1Pipeline} />
        </div>
        <TradingActionBoard
          buy={buyList}
          hold={holdList}
          sell={sellList}
          sellActive={sellActive}
          sellHistory={sellHistory}
          yesterday={yesterdayList}
          stopLossToday={stopLossList}
          sellUrgentCount={sellUrgent.length}
          sellTodayCount={sellHistory.length}
          activeStep={activeStep}
          profitLabel={profitLabel}
          profitRs={profitRs}
          holdPartialMin={holdEscapeMin}
          holdPartialMax={holdEscapeMax}
          holdMinutes={holdMinutes}
          stockQty={stockQty}
          slotsMax={slotsMax}
          volatileMode
          quantSlotCount={status?.basket?.slotsUsed ?? 0}
          liveStatus={livePrices.status}
          priceSource={livePrices.priceSource}
          strategyCompareMode={false}
          fullyAutomated
          slotsFree={status?.basket?.slotsFree}
          holdSlotCount={holdList.length}
          onRefreshSoldToday={loadSoldToday}
          soldTodayRefreshing={soldTodayRefreshing}
        />
      </main>
    </div>
  );
}
